import { NetworkSubordinate } from "./NetworkSubordinate";
import { HashSet } from "../Utils/HashSet";
import { Device } from "./Device";
import { MacToDeviceTable, MacToDeviceEntry } from "./MacToDeviceTable";
import { IpToDeviceTable, IpToDeviceEntry } from "./IpToDeviceTable";
import { DependentDeviceTable } from "./DependentDeviceTable";
import { Dictionary } from "../Utils/Dictionary";
import { Guid } from "../Utils/Guid";
import { IpAddress } from "./IpAddress";
import { MacAddress } from "./MacAddress";
import { IpToMacTable, IpToMacEntry } from "./IpToMacTable";
import { SystemInfo } from "../InfoSets/SystemInfo";
import { List } from "../Utils/List";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { DeviceKey } from "./Keys";
import { DeviceCategory } from "./DeviceCategory";
import { DependentDeviceId } from "./DependentDeviceId";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { DefaultRefResolver } from "./RefResolver";

//export interface DeviceEntryJson {
//	device: DeviceJson
//}

//export interface DeviceSetJson {
//	entries: DeviceEntryJson[]
//}

export class DeviceSet extends NetworkSubordinate implements Iterable<Device>, ObjectSerializer {

	//public static FromJson(json: DeviceSetJson, network?:Network) : DeviceSet {
	//	var result = new DeviceSet(network);

	//	json.entries.forEach((entry) => {
	//		result.Add(Device.FromJson(entry.device, network));
	//	});

	//	return result;
	//}

	private _deviceSet: HashSet<Device>;

	// INDEX into DEVICE SET (based on MAC ADDRESSES)
	private _macToDeviceTable: MacToDeviceTable;

	// INDEX into DEVICE SET (based on IP ADDRESSES)
	private _ipToDeviceTable: IpToDeviceTable;

	// INDEX into DEVICE SET (based on DEPENDENT DEVICE ID)
	private _dependentDeviceTable: DependentDeviceTable;

	// INDEX into DEVICE SET (based on GUID)
	private _guidToDeviceMap: Dictionary<Guid, Device>;

	private _netBiosNameIndex: DeviceIndex<NetBiosNameInfo>;
	private _systemNameIndex: DeviceIndex<SystemNameInfo>;
	private _hostNameIndex: DeviceIndex<HostNameInfo>;
	private _serialNumberIndex: DeviceIndex<SerialNumberInfo>;

	constructor(network: any) {
		super(network);
		this._deviceSet = new HashSet<Device>();
		this._macToDeviceTable = new MacToDeviceTable();
		this._ipToDeviceTable = new IpToDeviceTable();
		this._dependentDeviceTable = new DependentDeviceTable(this);
		this._guidToDeviceMap = new Dictionary<Guid, Device>();
		this._netBiosNameIndex = new DeviceIndex<NetBiosNameInfo>();
		this._systemNameIndex = new DeviceIndex<SystemNameInfo>();
		this._hostNameIndex = new DeviceIndex<HostNameInfo>();
		this._serialNumberIndex = new DeviceIndex<SerialNumberInfo>();
	}

	public ToArray(): Device[] {
		return this._deviceSet.ToList().Values;
	}

	public get Count(): number {
		return this._deviceSet.Count;
	}

	public get MacAddresses():HashSet<MacAddress> {
		return this._macToDeviceTable.MacAddresses;
	}

	public get IpAddresses(): HashSet <IpAddress> {
		return this._ipToDeviceTable.IpAddresses;
	}

	public Add(device:Device):void {
		if (!this._deviceSet.Contains(device)) {
			device.Network = this.Network;

			this._deviceSet.Add(device);

			this._macToDeviceTable.AddDevice(device);
			this._ipToDeviceTable.AddDevice(device);
			this._dependentDeviceTable.Add(device);
			this.AddToGuidIndex(device);
			this.AddToNetBiosNameIndex(device);
			this.AddToSystemNameIndex(device);
			this.AddToHostNameIndex(device);
			this.AddToSerialNumberIndex(device);
		}
	}

	public AddIpMacEntry(ipToMacEntry: IpToMacEntry): Device {
		var newDevice: Device = null;

		if (ipToMacEntry != null) {
			newDevice = new Device(this.Network);
			newDevice.IpAddress = ipToMacEntry.IpAddress.Clone();

			if (ipToMacEntry.HasMacAddress) {
					
				ipToMacEntry.MacAddresses.ForEach((macAddress) => {
					if (macAddress.IsValidAddress && !newDevice.HasMacAddress)
						newDevice.MacAddress = macAddress.Clone();
				});
			}

			if (ipToMacEntry.HasHostname) {
				var sysInfo: SystemInfo = newDevice.AddSystemInfo();
				sysInfo.Hostname = ipToMacEntry.Hostname.toString();
			}
		}

		return newDevice;
	}

	public Clear():void {
		this._macToDeviceTable.Clear();
		this._ipToDeviceTable.Clear();
		this._dependentDeviceTable.Clear();
		this._guidToDeviceMap.Clear();
		this._netBiosNameIndex.Clear();
		this._systemNameIndex.Clear();
		this._hostNameIndex.Clear();
		this._serialNumberIndex.Clear();

		this._deviceSet.ForEach((device) => {
			device.Network = null;
		});

		this._deviceSet.Clear();
	}

	public Remove(device: Device):void {
		if (this._deviceSet.Contains(device)) {
			device.Network = null;

			this._deviceSet.Remove(device);

			this._macToDeviceTable.RemoveDevice(device);
			this._ipToDeviceTable.RemoveDevice(device);
			this._dependentDeviceTable.Remove(device);
			this.RemoveFromGuidIndex(device);
			this.RemoveFromNetBiosNameIndex(device);
			this.RemoveFromSystemNameIndex(device);
			this.RemoveFromHostNameIndex(device);
			this.RemoveFromSerialNumberIndex(device);
		}
	}

	public FindByMacAddress(macAddress: MacAddress): List<Device> {
		if (!macAddress)
			return null;

		var macEntry: MacToDeviceEntry = this._macToDeviceTable.Find(macAddress);
		return macEntry ? macEntry.Devices : null;
	}

	public FindByGUID(deviceGUID: Guid): Device {
		var foundDevice: Device = null;

		this._deviceSet.Some((device) => {
			if (device.HasGUID && ObjUtils.Equals(device.GUID,deviceGUID)) {
				foundDevice = device;
				return true;;
			}
		});

		return foundDevice;
	}

	public FindUniqueByMacAddress(macAddress: MacAddress): Device {
		if (macAddress == null)
			return null;

		var device: Device = null;

		var macEntry: MacToDeviceEntry = this._macToDeviceTable.Find(macAddress);
		if ((macEntry != null) && macEntry.HasDevices) {
			if (macEntry.Devices.Count == 1) {
				device = macEntry.Devices.Get(0);
			}
			else {
				// TODO: DECIDE which device to send back
				device = macEntry.Devices.Get(0);
			}
		}

		return (device);
	}

	public ContainsDeviceWithIpAddress(ipAddress:IpAddress):boolean{
		if (ipAddress == null)
			return false;

		var ipEntry: IpToDeviceEntry = this._ipToDeviceTable.Find(ipAddress);
		if ((ipEntry != null) && (ipEntry.Devices.Count > 0))
			return true;

		return false;
	}

	public FindByIpAddress(ipAddress: IpAddress): List<Device> {
		if (!ipAddress)
			return null;

		var ipEntry: IpToDeviceEntry = this._ipToDeviceTable.Find(ipAddress);
		if (ipEntry != null)
			return ipEntry.Devices;
		else
			return null;
	}

	public FindUniqueByIpAddress(ipAddress: IpAddress):Device{
		if (ipAddress == null)
			return null;

		var ipDevice: Device = null;

		var ipEntry: IpToDeviceEntry = this._ipToDeviceTable.Find(ipAddress);
		if (ipEntry != null) {
			if (ipEntry.HasDevices) {
				this._deviceSet.Some((device) => {
					if (ipDevice == null)
						ipDevice = device;
					else {
						if (device.IsSnmpDevice())
							ipDevice = device;
					}

					if (ipDevice.IsSnmpDevice()) {
						if (ipDevice.HasIfTable)
							return true;
					}
				});
			}
		}

		return ipDevice;
	}

	public FindUniqueByDeviceKey(deviceKey: DeviceKey): Device {
		if (!deviceKey)
			return null;

		var device:Device = deviceKey.Resolve(this);

		return device;
	}

	public FindUniqueByHostname(hostname:string): Device {
		if (!hostname)
			return null;

		var device:Device = null;

		var hostInfo = new HostNameInfo(hostname);

		var deviceList: List<Device> = this.FindByHostName(hostInfo);
		if ((deviceList != null) && (deviceList.Count == 1))
			device = deviceList.Get(0);

		return device;
	}

	public FindByCategory(deviceCategory: DeviceCategory):List<Device> {
		var deviceList = new List<Device>();

		this._deviceSet.ForEach((device) => {
			if (deviceCategory == null) {
				if (device.Categories.Count > 0)
					return;
			} else {
				if (!device.Categories.Contains(deviceCategory))
					return;
			}

			deviceList.Add(device);
		});

		return deviceList;
	}

    public FindDependentDevicesByControllerDevice(controller: Device): List<Device> {
        return this._dependentDeviceTable.FindByControllerDevice(controller);
    }

    public FindDependentDevicesById(id: DependentDeviceId): List<Device> {
        return this._dependentDeviceTable.FindByDependentDeviceId(id);
    }

    public FindDependentDevices(controllerKey: DeviceKey): List<Device> {
        return this._dependentDeviceTable.FindByControllerDeviceKey(controllerKey);
    }

	public FindByInfoSet(infoSetName:string):List<Device> {
		var deviceList = new List<Device>();

		this._deviceSet.ForEach((device) => {
			if (!device.HasInfoSet(infoSetName))
				return;

			deviceList.Add(device);
		});

		return deviceList;
	}

	public get IpSightings(): HashSet<IpAddress> {
		var ipSightings = new HashSet<IpAddress>();

		this._deviceSet.ForEach((device) => {
			ipSightings.UnionWith(device.IpSightings);
		});

		return ipSightings;
	}

	public get OverlappedIpAddresses(): HashSet<IpAddress> {
		var duplicateIPs = new HashSet<IpAddress>();

		this._ipToDeviceTable.ForEach((ipEntry) => {
			if (ipEntry.Devices.Count <= 1)
				return;

			duplicateIPs.Add(ipEntry.IpAddress);
		});

		return (duplicateIPs);
	}

	public get MacSightings(): HashSet<MacAddress> {
		var macSightings = new HashSet<MacAddress>();

		this._deviceSet.ForEach((device) => {
			macSightings.UnionWith(device.MacSightings);
		});

		return macSightings;
	}

	public get OverlappedMacAddresses(): HashSet<MacAddress> {
		var duplicateMacs = new HashSet<MacAddress>();

		this._macToDeviceTable.ForEach((macEntry) => {
			if (macEntry.Devices.Count <= 1)
				return;

			duplicateMacs.Add(macEntry.MacAddress);
		});

		return (duplicateMacs);
	}

	public RebuildIndexes():void {
		this._macToDeviceTable.Clear();
		this._ipToDeviceTable.Clear();
		this._dependentDeviceTable.Clear();
		this._guidToDeviceMap.Clear();
		this._netBiosNameIndex.Clear();
		this._systemNameIndex.Clear();
		this._hostNameIndex.Clear();
		this._serialNumberIndex.Clear();

		this._deviceSet.ForEach((device) => {
			this._macToDeviceTable.AddDevice(device);
			this._ipToDeviceTable.AddDevice(device);
			this._dependentDeviceTable.Add(device);
			this.AddToGuidIndex(device);
			this.AddToNetBiosNameIndex(device);
			this.AddToSystemNameIndex(device);
			this.AddToHostNameIndex(device);
			this.AddToSerialNumberIndex(device);
		});
	}

	public RemoveDuplicates():void {
		// RESOLVE DUPLICATE/OVERLAPPED MAC ADDRESSES in the _macToDeviceTable
		var duplicateMacAddresses:List<MacToDeviceEntry> = this._macToDeviceTable.GetDuplicateMacAddresses();
		duplicateMacAddresses.ForEach((macEntry) => { 
			// VERIFY that the MAC is still in the MAIN INDEX
			var duplicateMacEntry: MacToDeviceEntry = this._macToDeviceTable.Find(macEntry.MacAddress);
			if (duplicateMacEntry == null)
				return;

			// VERIFY that it STILL has MORE than 1 Device to RESOLVE
			if (duplicateMacEntry.Devices.Count <= 1)
				return;

			this.RemoveAllDuplicateDevices(duplicateMacEntry);
		});


		// RESOLVE DUPLICATE/OVERLAPPED IP ADDRESSES in the _ipToDeviceTable
		var duplicateIpAddresses: List<IpToDeviceEntry> = this._ipToDeviceTable.GetDuplicateIpAddresses();
		duplicateIpAddresses.ForEach((ipEntry) => {
			// VERIFY that the IP is still in the MAIN INDEX
			var duplicateIpEntry: IpToDeviceEntry = this._ipToDeviceTable.Find(ipEntry.IpAddress);
			if (duplicateIpEntry == null)
				return;

			// VERIFY that this IP STILL has MORE than 1 Device to RESOLVE
			if (duplicateIpEntry.Devices.Count <= 1)
				return;

			this.RemoveAllDuplicateDevicesByIp(duplicateIpEntry);
		});
	}

	private RemoveNonSnmpDevices(deviceList:List<Device>) :number {
		var removeCount = 0;

		if ((deviceList != null) && (deviceList.Count > 0)) {
			var toRemoveList = new List<Device>();

			deviceList.ForEach((device) => {
				if (device.IsSnmpDevice())
					return;

				if (!toRemoveList.Contains(device))
					toRemoveList.Add(device);
			});

			// REMOVE from the DEVICE LIST (and INDEXES)
			toRemoveList.ForEach((device) => {
				this.Remove(device);
				removeCount++;
			});
		}

		return (removeCount);
	}

	private RemoveAllDuplicateDevices(macEntry: MacToDeviceEntry):void {
		if ((macEntry == null) || (macEntry.Devices.Count <= 1))
			return;

		// STEP 1: If we can TALK to any of the DEVICES in the list via SNMP; we want to get rid of anything NOT SNMP (i.e. IP devices)
		if (macEntry.HasSnmpDevice())
			this.RemoveNonSnmpDevices(macEntry.Devices);

		// CHECK to see if we have RESOLVED the DUPLICATEs
		if (macEntry.Devices.Count <= 1)
			return;

		var toRemoveList = new List<Device>();
		var srcDevice: Device = null;

		// STEP 2: REMOVE all DUPLICATE DEVICES (the last in the list is considered the MOST current)
		var deviceArray: List<Device> = macEntry.Devices;
		for (var I = (deviceArray.Count - 1); I >= 0; I--) {
			srcDevice = deviceArray.Get(I);

			// CHECK to make sure it is still IN the list
			if (!macEntry.Devices.Contains(srcDevice))
				continue;

			toRemoveList.Clear();

			// GET the duplicate DEVICES
			macEntry.DuplicateDevices(srcDevice).ForEach((device) => {
				if (!toRemoveList.Contains(device))
					toRemoveList.Add(device);
			});

			// CLEAN up the DEVICE (as well as INDEXES)
			toRemoveList.ForEach((device) => {
					this.Remove(device);
			});
		}
	}

	private RemoveAllDuplicateDevicesByIp(ipToDeviceEntry: IpToDeviceEntry ):void {
		if ((ipToDeviceEntry == null) || (ipToDeviceEntry.Devices.Count <= 1))
			return;

		// STEP 1: If we can TALK to any of the DEVICES in the list via SNMP; we want to get rid of anything NOT SNMP (i.e. IP devices)
		if (ipToDeviceEntry.HasSnmpDevice())
			this.RemoveNonSnmpDevices(ipToDeviceEntry.Devices);

		// CHECK to see if we have RESOLVED the DUPLICATEs
		if (ipToDeviceEntry.Devices.Count <= 1)
			return;

		var toRemoveList = new List<Device>();
		var srcDevice: Device = null;

		// STEP 2: REMOVE all DUPLICATE DEVICES (the last in the list is considered the MOST current)
		var deviceArray: List<Device> = ipToDeviceEntry.Devices;
		for (var I = (deviceArray.Count - 1); I >= 0; I--)
		{
			srcDevice = deviceArray.Get(I);

			// CHECK to make sure it is still IN the list
			if (!ipToDeviceEntry.Devices.Contains(srcDevice))
				continue;

			toRemoveList.Clear();

			// GET the duplicate DEVICES
			ipToDeviceEntry.DuplicateDevices(srcDevice).ForEach((device) => {
				if (!toRemoveList.Contains(device))
					toRemoveList.Add(device);
			});

			// CLEAN up the DEVICE (as well as INDEXES)
			toRemoveList.ForEach((device) => {
				this.Remove(device);
			});
		}
	}

	public VerifyGUIDs():void {
		this._guidToDeviceMap.Clear();

		this._deviceSet.ForEach((device) => {
			device.VerifyGUID();
			this._guidToDeviceMap.Add(device.GUID, device);
		});
	}

	private AddToGuidIndex(device:Device):boolean {
		if (device == null)
			return false;
		if (!device.HasGUID)
			return false;

		// CHECK the existing KEYS and ADD
		if (!this._guidToDeviceMap.ContainsKey(device.GUID))
		{
			this._guidToDeviceMap.Add(device.GUID, device);
			return true;
		}

		// NOTE: The KEY must already EXIST (verify that it is the same device I am adding)
		var oldDevice: Device = this._guidToDeviceMap.Item(device.GUID);
		if (oldDevice == device)
			return true;

		return false;
	}

	private RemoveFromGuidIndex(device:Device):void {
		if (device == null)
			return;
		if (!device.HasGUID)
			return;
		this._guidToDeviceMap.Remove(device.GUID);
	}

	private AddToNetBiosNameIndex(device:Device):void {
		if (device.HasSystemInfo)
		{
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasNetBiosName)
			{
				var nbName = new NetBiosNameInfo(sysInfo.NetBiosName, sysInfo.NetBiosDomain);
				this._netBiosNameIndex.Add(nbName, device);
			}
		}
	}

	private RemoveFromNetBiosNameIndex(device:Device ):void {
		if (device.HasSystemInfo)
		{
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasNetBiosName)
			{
				var nbName = new NetBiosNameInfo(sysInfo.NetBiosName, sysInfo.NetBiosDomain);
				this._netBiosNameIndex.Remove(nbName, device);
			}
		}
	}

	private AddToSystemNameIndex(device:Device):void {
		if (device.HasSystemInfo)
		{
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasSystemName)
			{
				var sysName: SystemNameInfo = null;

				if (sysInfo.HasSystemOID)
					sysName = new SystemNameInfo(sysInfo.SystemName, sysInfo.SystemOID);
				else
					sysName = new SystemNameInfo(sysInfo.SystemName);

				this._systemNameIndex.Add(sysName, device);
			}
		}
	}

	private RemoveFromSystemNameIndex(device: Device):void{
		if (device.HasSystemInfo) {
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasSystemName && sysInfo.HasSystemOID) {
				var sysName = new SystemNameInfo(sysInfo.SystemName, sysInfo.SystemOID);
				this._systemNameIndex.Remove(sysName, device);
			}
		}
	}

	private AddToHostNameIndex(device: Device):void {
		if (device.HasSystemInfo) {
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasHostname) {
				var hostName = new HostNameInfo(sysInfo.Hostname);
				this._hostNameIndex.Add(hostName, device);
			}
		}
	}

	private RemoveFromHostNameIndex(device: Device):void {
		if (device.HasSystemInfo) {
			var sysInfo: SystemInfo = device.SystemInfo;
			if (sysInfo.HasHostname) {
				var hostName = new HostNameInfo(sysInfo.Hostname);
				this._hostNameIndex.Remove(hostName, device);
			}
		}
	}

	private AddToSerialNumberIndex(device: Device): void {
		device.SerialNumbers.ForEach((serNumInfo) => {
			this._serialNumberIndex.Add(serNumInfo, device);
		});
	}

	private RemoveFromSerialNumberIndex(device: Device):void {
		device.SerialNumbers.ForEach((serNumInfo) => {
			this._serialNumberIndex.Remove(serNumInfo, device);
		});
	}

	public get NetBiosNames(): HashSet<NetBiosNameInfo> {
		return this.ExtractKeys(this._netBiosNameIndex);
	}

	public get OverlappedNetBiosNames(): HashSet<NetBiosNameInfo> {
		return this.ExtractKeys(this._netBiosNameIndex.Duplicates);
	}

	public FindByNetBiosName(nbName: NetBiosNameInfo): List<Device> {
		return this.FindByKey(nbName, this._netBiosNameIndex);
	}

	public get SystemNames(): HashSet<SystemNameInfo> {
		return this.ExtractKeys(this._systemNameIndex);
	}

	public get OverlappedSystemNames():HashSet < SystemNameInfo > {
		return this.ExtractKeys(this._systemNameIndex.Duplicates);
	}

	public FindBySystemName(sysName: SystemNameInfo): List<Device> {
		return this.FindByKey(sysName, this._systemNameIndex);
	}

	public get HostNames(): HashSet < HostNameInfo > {
		return this.ExtractKeys(this._hostNameIndex);
	}

	public get OverlappedHostNames():HashSet < HostNameInfo > {
		return this.ExtractKeys(this._hostNameIndex.Duplicates);
	}

	public FindByHostName(hostName: HostNameInfo): List<Device> {
		return this.FindByKey(hostName, this._hostNameIndex);
	}

	public get SerialNumbers():HashSet <SerialNumberInfo>
	{
		return this.ExtractKeys(this._serialNumberIndex);
	}

	public get OverlappedSerialNumbers():HashSet < SerialNumberInfo > {
		return this.ExtractKeys(this._serialNumberIndex.Duplicates);
	}

	public FindBySerialNumber(serialNumber: SerialNumberInfo):List<Device>{
		return this.FindByKey(serialNumber, this._serialNumberIndex);
	}

	private ExtractKeys<KeyType>(entries: Iterable<DeviceIndexEntry<KeyType>>): HashSet<KeyType> {
		var result = new HashSet<KeyType>();
		entries.ForEach((entry) => {
			result.Add(entry.Key);
		});
		return result;
	}

	private FindByKey<KeyType>(key: KeyType, index: DeviceIndex<KeyType>): List<Device> {
		if (key == null)
			return null;

		var entry: DeviceIndexEntry<KeyType> = index.Find(key);
		if (entry != null)
			return entry.Devices;
		else
			return null;
	}

	public ForEach(callbackfn: (value: Device, index: number, array: Device[]) => void, thisArg?: any): void {
		this._deviceSet.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: Device, index: number, array: Device[]) => boolean, thisArg?: any): boolean {
		return this._deviceSet.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: Device, index: number, array: Device[]) => boolean, thisArg?: any): boolean {
		return this._deviceSet.Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.devices = {};
		obj = obj.devices;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._deviceSet, context); 
	}

	public Read(obj: any, context: any): void {
		if (!obj.devices)
			throw new Error();
		obj = obj.devices;

		this.Clear();

		let loadState = new LoadState(this);

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				loadState.LoadDevice(value, context);
			});
		}

		if (loadState.DepDevs.Count > 0) {
			let refResolver = new DefaultRefResolver(this);

			loadState.DepDevs.ForEach((depDev: Device) => {
				depDev.DependentDeviceId.ControllerDeviceRef.Resolve(refResolver);
			}, this);

			loadState.DepDevs.ForEach((depDev: Device) => {
				this.Add(depDev);
			}, this);
		}
	}
}

class LoadState {
	public DevSet: DeviceSet;
	public DepDevs: List<Device>;

	constructor(devSet: DeviceSet) {
		this.DevSet = devSet;
		this.DepDevs = new List<Device>();
	}

	public LoadDevice(obj: any, context: any): void {
		let device = new Device(this.DevSet.Network);
		device.Read(obj, context);
		if (device.HasDependentDeviceId)
			this.DepDevs.Add(device);
		else
			this.DevSet.Add(device);
	}
}

//
// DeviceIndex
//
export class DeviceIndex<KeyType> implements Iterable<DeviceIndexEntry<KeyType>>
{
	private _Entries: Dictionary<KeyType, DeviceIndexEntry<KeyType>> ;
		
	constructor() {
		this._Entries = new Dictionary<KeyType, DeviceIndexEntry<KeyType>>();
	}

	public Add(key: KeyType, device: Device): DeviceIndexEntry<KeyType> {
		var entry: DeviceIndexEntry<KeyType> = null;

		if (this._Entries.ContainsKey(key)) {
			entry = this._Entries.Item(key);
			if (entry) {
				if (!entry.Devices.Contains(device))
					entry.Devices.Add(device);
			}
		}
		else {
			entry = new DeviceIndexEntry<KeyType>(key, device);
			this._Entries.Add(key, entry);
		}

		return (entry);
	}

	public Clear():void {
		this._Entries.Clear();
	}

	public get Count() {
		return this._Entries.Count;
	}

	public Find(key: KeyType): DeviceIndexEntry<KeyType>  {
		return this._Entries.Item(key);
	}

	public Remove(key: KeyType, device: Device):void {
		var entryRemoved = false;

		var entry: DeviceIndexEntry<KeyType> = this._Entries.Item(key);

		if(entry) {

			entry.RemoveDevice(device);

			if (!entry.HasDevices) {
				this._Entries.Remove(key);
			}
		}
	}

	public get Duplicates() : List<DeviceIndexEntry<KeyType>> {
		var duplicates = new List<DeviceIndexEntry<KeyType>>();

		this._Entries.ForEach((pair) => {
			if (pair.Value.Devices.Count > 1) {
				duplicates.Add(pair.Value);
			}
		});
		return (duplicates);
	}

	public ForEach(callbackfn: (value: DeviceIndexEntry<KeyType>, index: number, array: DeviceIndexEntry<KeyType>[]) => void, thisArg?: any): void {
		this._Entries.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: DeviceIndexEntry<KeyType>, index: number, array: DeviceIndexEntry<KeyType>[]) => boolean, thisArg?: any): boolean {
		return this._Entries.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: DeviceIndexEntry<KeyType>, index: number, array: DeviceIndexEntry<KeyType>[]) => boolean, thisArg?: any): boolean {
		return this._Entries.Values().Every(callbackfn, thisArg);
	}
}

export class DeviceIndexEntry<KeyType>
{
	public Key: KeyType;

	public Devices: List<Device>;

	public get HasDevices(): boolean {
		return (this.Devices != null) && (this.Devices.Count > 0);
	}

	constructor(key:KeyType, device ?:Device)	{
		this.Key = key;
		this.Devices = new List<Device>();
		if(device)
			this.Devices.Add(device);
	}

	public AddDevice(device:Device):void {
		if (!this.Devices.Contains(device))
			this.Devices.Add(device);
	}

	public RemoveDevice(device:Device):void{
		this.Devices.Remove(device);
	}
}

export class NetBiosNameInfo implements IComparable {
	public NetBiosName: string; 
	public get HasNetBiosName() : boolean {
		return !this.NetBiosName;
	}

	public NetBiosDomain: string;
	public get HasNetBiosDomain() : boolean {
		return !!this.NetBiosDomain;
	}


	constructor(nbName: string, nbDomain: string) {
		this.NetBiosName = nbName;
		if (!this.NetBiosName)
			this.NetBiosName = "";

		this.NetBiosDomain = nbDomain;
		if (!this.NetBiosDomain)
			this.NetBiosDomain = "";
	}

	public Equals(obj:any): boolean {
		if (obj == null)
			return false;
		if (obj == this)
			return true;
		if (!(obj instanceof NetBiosNameInfo))
			return false;

		var other = <NetBiosNameInfo>obj;

        return ObjUtils.Equals(this.NetBiosName, other.NetBiosName) &&
            ObjUtils.Equals(this.NetBiosDomain, other.NetBiosDomain);
	}

	public GetHashCode() : number {
		return (ObjUtils.GetHashCode(this.NetBiosName) ^ ObjUtils.GetHashCode(this.NetBiosDomain));
	}

	public CompareTo(other: NetBiosNameInfo) : number {
		if (!other) {
			return 1;
		}
		else {
			var cmp = ObjUtils.CompareTo(this.NetBiosName, other.NetBiosName);
			if (cmp == 0)
				cmp = ObjUtils.CompareTo(this.NetBiosDomain, other.NetBiosDomain);
			return cmp;
		}
	}

	public toString() : string
	{
		if (this.HasNetBiosDomain)
			return this.NetBiosName + ", " + this.NetBiosDomain;
		else
			return this.NetBiosName;
	}
}

export class SystemNameInfo implements IComparable {
		
	public SystemName: string;
	public get HasSystemName(): boolean {
		return !!this.SystemName;
	}
		
	public SystemOID: string;
	public get HasSystemOID() : boolean {
		return !!this.SystemOID; 
	}

	constructor(sysName:string, sysOid?:string)
	{
		this.SystemName = sysName;
		if (this.SystemName == null)
			this.SystemName = "";

		this.SystemOID = sysOid;
		if (this.SystemOID == null)
			this.SystemOID = "";
	}

	public Equals(obj:any):boolean {
		if (obj == null)
			return false;
		if (obj == this)
			return true;
		if (!(obj instanceof SystemNameInfo))
			return false;

		var other = <SystemNameInfo>obj;

        return ObjUtils.Equals(this.SystemName, other.SystemName) &&
            ObjUtils.Equals(this.SystemOID, other.SystemOID);
	}

	public GetHashCode() : number {
		return (ObjUtils.GetHashCode(this.SystemName) ^ ObjUtils.GetHashCode(this.SystemOID));
	}

	public CompareTo(other:SystemNameInfo):number {
		if (!other) {
			return 1;
		} else {
			var cmp = ObjUtils.CompareTo(this.SystemName, other.SystemName);
			if (cmp == 0)
				cmp = ObjUtils.CompareTo(this.SystemOID, other.SystemOID);
			return cmp;
		}
	}

	public toString():string {
		if (this.HasSystemOID)
			return this.SystemName + ", " + this.SystemOID;
		else
			return this.SystemName;
	}
}

export class HostNameInfo implements IComparable
{
	public HostName: string;
	public get HasHostName() : boolean {
		return (!!this.HostName); 
	}

	constructor(hostName: string) {
		this.HostName = hostName;
		if (this.HostName == null)
			this.HostName = "";
	}

	public Equals(obj: any): boolean {
		if (obj == null)
			return false;
		if (obj == this)
			return true;
		if (!(obj instanceof HostNameInfo))
			return false;

		var other = <HostNameInfo>obj;

		return (ObjUtils.Equals(this.HostName, other.HostName));
	}

	public GetHashCode(): number {
		return ObjUtils.GetHashCode(this.HostName);
	}

	public CompareTo(other: HostNameInfo) : number {
		if (other == null)
			return 1;
		else
			return ObjUtils.CompareTo(this.HostName, other.HostName);
	}

	public toString():string {
		return this.HostName;
	}
}

export class SerialNumberInfo implements IComparable {

	public SerialNumber: string;
	public get HasSerialNumber(): boolean {
		return (!!this.SerialNumber);
	}

	constructor(serialNumber:string) {
		this.SerialNumber = serialNumber;
		if (!this.SerialNumber)
			this.SerialNumber = "";
	}

	public Equals(obj:any) : boolean {
		if (obj == null)
			return false;
		if (obj == this)
			return true;
		if (!(obj instanceof SerialNumberInfo))
			return false;

		var other = <SerialNumberInfo>obj;

		return (ObjUtils.Equals(this.SerialNumber, other.SerialNumber));
	}

	public GetHashCode():number {
		return ObjUtils.GetHashCode(this.SerialNumber);
	}

	public CompareTo(other:SerialNumberInfo) {
		if (other == null)
			return 1;
		else
			return ObjUtils.CompareTo(this.SerialNumber, other.SerialNumber);
	}

	public toString() : string {
		return this.SerialNumber;
	}
}
